import { pathUtils } from './pathUtils';

export interface ProjectFile {
    name: string;    // Includes full path, e.g., "src/main.mo"
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
    // Fixed file system types to match other files
    fileSystem?: 'backend' | 'frontend' | 'asset';
    assetKey?: string;
    assetUrl?: string;
    displayIcon?: string;
    displayColor?: string;
    tooltipSuffix?: string;
    mimeType?: string;
    size?: number;
}

interface FileTreeNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileTreeNode[];
    originalFile?: ProjectFile;
    // Fixed file system types to match other files
    fileSystemType?: 'backend' | 'frontend' | 'asset';
}

/**
 * Builds a file tree for UI display while preserving exact backend paths
 * @param files The list of files to build a tree from
 * @returns A file tree structure for UI
 */
// FIXED: Enhanced buildFileTree function with proper virtual folder handling

// Add this debug code to your buildFileTree function to compare working vs broken folders

export const buildFileTree = (files: ProjectFile[]): FileTreeNode => {
    // console.log('=== BUILD FILE TREE COMPARISON DEBUG ===');

    // Log all files being processed
    // console.log('All input files:');
    // files.forEach((file, index) => {
    //     console.log(`${index + 1}. ${file.name} (${file.fileSystem}) [${file.mimeType}]`);
    // });

    // Separate asset files and virtual folders
    const assetFiles = files.filter(f => f.fileSystem === 'asset' && f.mimeType !== 'folder');
    const assetFolders = files.filter(f => f.fileSystem === 'asset' && f.mimeType === 'folder');

    // console.log('\nAsset files (will create implicit folders):');
    // assetFiles.forEach((file, index) => {
    //     const dirPath = file.name.substring(0, file.name.lastIndexOf('/'));
        // console.log(`${index + 1}. File: ${file.name}`);
        // console.log(`   -> Will create folder: ${dirPath}`);
    // });

    // console.log('\nAsset virtual folders (should be explicit):');
    // assetFolders.forEach((file, index) => {
        // console.log(`${index + 1}. Virtual folder: ${file.name} (${file.fileName})`);
    // });

    const root: FileTreeNode = {
        name: '/',
        path: '/',
        isDirectory: true,
        children: [],
        fileSystemType: 'backend'
    };

    // Helper function to ensure directory path exists
    const ensureDirectoryPath = (fullPath: string, fileSystem?: 'backend' | 'frontend' | 'asset'): FileTreeNode => {
        // console.log(`\n🔧 ensureDirectoryPath called with: "${fullPath}" (${fileSystem})`);

        const parts = fullPath.split('/').filter(Boolean);
        let currentNode = root;
        let currentPath = '';

        for (const part of parts) {
            currentPath = currentPath ? `${currentPath}/${part}` : part;

            let node = currentNode.children?.find(
                child => child.name === part && child.isDirectory
            );

            if (!node) {
                // FIXED: Determine fileSystemType based on path and provided fileSystem
                let nodeFileSystemType: 'backend' | 'frontend' | 'asset' = 'backend';

                if (fileSystem) {
                    nodeFileSystemType = fileSystem;
                } else if (currentPath.includes('/frontend/') || currentPath.includes('/assets/')) {
                    nodeFileSystemType = 'frontend';
                }

                node = {
                    name: part,
                    path: currentPath,
                    isDirectory: true,
                    children: [],
                    fileSystemType: nodeFileSystemType
                };

                // console.log(`  📁 Created directory node: ${part} at ${currentPath} (${nodeFileSystemType})`);
                currentNode.children?.push(node);
            } else {
                // console.log(`  ♻️  Directory already exists: ${part} at ${currentPath}`);
            }
            currentNode = node;
        }
        return currentNode;
    };

    // Group files by directory
    const filesByDirectory = new Map<string, ProjectFile[]>();

    files.forEach(file => {
        if (!file || !file.name) return;

        const dirPath = file.name.substring(0, file.name.lastIndexOf('/'));
        if (!filesByDirectory.has(dirPath)) {
            filesByDirectory.set(dirPath, []);
        }
        filesByDirectory.get(dirPath)?.push(file);
    });

    // Create directory structure
    // console.log('\n📂 Creating directory structure from files:');
    filesByDirectory.forEach((dirFiles, dirPath) => {
        if (dirPath) {
            // FIXED: Pass the file system type when creating directory structure
            const firstFile = dirFiles[0];
            // console.log(`Creating directory structure for: ${dirPath} (from ${firstFile.fileSystem} file)`);
            ensureDirectoryPath(dirPath, firstFile?.fileSystem);
        }
    });

    // Add files to their directories
    files.forEach(file => {
        if (!file || !file.name) return;

        // Skip .keep files (they're filtered out for display)
        if (file.fileName === '.keep' || file.name.endsWith('/.keep')) {
            // console.log(`⏭️  Skipping .keep file: ${file.name}`);
            return;
        }

        // FIXED: Handle virtual folders - process them as directories
        if (file.mimeType === 'directory' || file.mimeType === 'folder') {
            // console.log(`\n🗂️  Processing virtual folder: ${file.name}`);

            // Ensure the directory structure exists for this virtual folder
            const folderNode = ensureDirectoryPath(file.name, file.fileSystem);

            // FIXED: Set the fileSystemType based on the virtual folder's fileSystem
            folderNode.fileSystemType = file.fileSystem;

            // FIXED: Store the original file reference so visibility logic can access it
            folderNode.originalFile = file;

            // CRITICAL: Mark as directory to ensure proper handling
            folderNode.isDirectory = true;

            // console.log(`✅ Processed virtual folder:`, {
            //     name: file.name,
            //     fileName: file.fileName,
            //     fileSystem: file.fileSystem,
            //     folderNodeType: folderNode.fileSystemType,
            //     hasOriginalFile: !!folderNode.originalFile,
            //     nodePath: folderNode.path
            // });

            return; // Don't process as a regular file
        }

        const dirPath = file.name.substring(0, file.name.lastIndexOf('/'));
        const fileName = file.name.substring(file.name.lastIndexOf('/') + 1);

        // console.log(`\n📄 Processing regular file: ${fileName} in ${dirPath}`);

        const parentNode = dirPath ? ensureDirectoryPath(dirPath, file.fileSystem) : root;

        // Check if file already exists (avoid duplicates)
        const existingFile = parentNode.children?.find(
            child => child.name === fileName && !child.isDirectory
        );

        if (!existingFile) {
            parentNode.children?.push({
                name: fileName,
                path: file.name,
                isDirectory: false,
                originalFile: file,
                fileSystemType: file.fileSystem
            });
            // console.log(`✅ Added file node: ${fileName}`);
        } else {
            // console.log(`♻️  File already exists: ${fileName}`);
        }
    });

    // Sort children in each directory
    const sortChildren = (node: FileTreeNode) => {
        if (node.children) {
            node.children.sort((a, b) => {
                if (a.isDirectory !== b.isDirectory) {
                    return a.isDirectory ? -1 : 1;
                }
                return a.name.localeCompare(b.name);
            });

            node.children.forEach(child => {
                if (child.isDirectory) {
                    sortChildren(child);
                }
            });
        }
    };

    sortChildren(root);

    // console.log('\n=== FINAL TREE STRUCTURE ===');
    const logTree = (node: FileTreeNode, depth = 0) => {
        const indent = '  '.repeat(depth);
        const icon = node.isDirectory ? '📁' : '📄';
        const type = node.fileSystemType || 'unknown';
        const hasOriginal = !!node.originalFile;
        // console.log(`${indent}${icon} ${node.name} (${type}) [original: ${hasOriginal}]`);

        if (node.children) {
            node.children.forEach(child => logTree(child, depth + 1));
        }
    };
    logTree(root);

    // console.log('=== BUILD FILE TREE COMPARISON DEBUG END ===');
    return root;
};

/**
 * Efficiently updates a file tree when a file is modified but structure hasn't changed
 * @param existingTree The current file tree
 * @param files The updated list of files
 * @param modifiedFilePath The path of the file that was modified
 * @returns The updated file tree, or the existing one if only content changed
 */
export const updateFileTreeForFile = (
    existingTree: FileTreeNode,
    files: ProjectFile[],
    modifiedFilePath: string
): FileTreeNode => {
    // If we don't have an existing tree, build from scratch
    if (!existingTree || !existingTree.children) {
        return buildFileTree(files);
    }

    // For simple content updates of existing files, no need to rebuild the tree
    // Use exact path matching without normalization
    const isNewFile = !files.some(f => f.name === modifiedFilePath);
    const isDeletedFile = files.every(f => f.name !== modifiedFilePath);

    // If it's just a content update, return the existing tree
    if (!isNewFile && !isDeletedFile) {
        return existingTree;
    }

    // For structural changes (new file or deleted file), rebuild the tree
    return buildFileTree(files);
};

/**
 * Updates the file tree after a file save operation
 * @param existingTree The current file tree
 * @param files The updated list of files
 * @param savedFilePath The path of the file that was saved
 * @returns The updated file tree
 */
export const updateFileTreeAfterSave = (
    existingTree: FileTreeNode,
    files: ProjectFile[],
    savedFilePath: string
): FileTreeNode => {
    // If we don't have an existing tree, build from scratch
    if (!existingTree || !existingTree.children) {
        return buildFileTree(files);
    }

    // Find the file in the list using exact path comparison
    const fileExists = files.some(f => f.name === savedFilePath);

    // If this is an existing file update, we can return the current tree
    if (fileExists && !savedFilePath.endsWith('.keep')) {
        // Use exact paths without normalization for directory lookup
        const dirPath = pathUtils.getParentPath(savedFilePath);
        const fileName = pathUtils.getFileName(savedFilePath);

        // Check if the file already exists in tree structure
        const dirExists = findNodeByPath(existingTree, dirPath);
        if (dirExists && dirExists.children) {
            const fileNode = dirExists.children.find(child =>
                !child.isDirectory && child.name === fileName
            );

            // If file node already exists, just return existing tree
            if (fileNode) {
                return existingTree;
            }
        }
    }

    // For new files or any structural changes, rebuild the tree
    return buildFileTree(files);
};

/**
 * Helper function to find a node by path in the file tree
 * @param root The root node of the tree
 * @param path The path to search for
 * @returns The node if found, undefined otherwise
 */
export const findNodeByPath = (
    root: FileTreeNode,
    path: string
): FileTreeNode | undefined => {
    if (!path || path === '/' || path === '') {
        return root;
    }

    const parts = path.split('/').filter(Boolean);
    let currentNode: FileTreeNode | undefined = root;

    for (const part of parts) {
        if (!currentNode || !currentNode.children) {
            return undefined;
        }

        currentNode = currentNode.children.find(
            child => child.name === part && child.isDirectory
        );

        if (!currentNode) {
            return undefined;
        }
    }

    return currentNode;
};

/**
 * Filters a file tree based on a search query
 * @param node The root node of the file tree
 * @param query The search query (case-insensitive)
 * @returns A filtered file tree, or null if no matches
 */
export const filterFileTree = (
    node: FileTreeNode,
    query: string
): FileTreeNode | null => {
    if (!query || query.trim() === '') {
        return node;
    }

    const searchQuery = query.toLowerCase().trim();
    const filteredNode: FileTreeNode = {
        ...node,
        children: []
    };

    if (node.children) {
        for (const child of node.children) {
            // Check if the node name or path matches the query
            const matchesQuery = 
                child.name.toLowerCase().includes(searchQuery) ||
                child.path.toLowerCase().includes(searchQuery) ||
                (child.originalFile?.fileName?.toLowerCase().includes(searchQuery));

            if (child.isDirectory) {
                // Recursively filter children
                const filteredChild = filterFileTree(child, query);
                if (filteredChild && (filteredChild.children?.length || matchesQuery)) {
                    // Include directory if it matches or has matching children
                    filteredNode.children!.push(matchesQuery ? child : filteredChild);
                }
            } else {
                // Include file if it matches
                if (matchesQuery) {
                    filteredNode.children!.push(child);
                }
            }
        }
    }

    // Return null if no matches found, otherwise return filtered node
    if (filteredNode.children!.length === 0 && !node.name.toLowerCase().includes(searchQuery)) {
        return null;
    }

    return filteredNode;
};